import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useIsMobile } from "../../hooks/useIsMobile";

// Visual-first work-reel card.
//
// Modes:
//   1. External card (project.externalUrl + project.youtubeId)
//      - Default: YouTube thumbnail (maxres → hqdefault fallback)
//      - Hover (desktop): a small "GIF" preview built by cycling through
//        YouTube's auto-generated storyboard frames (/1.jpg, /2.jpg, /3.jpg).
//        Crossfaded → feels like a tiny looping GIF extracted from the video.
//      - Click: opens the original URL in a new tab (any platform, not just YT)
//   2. MP4 video (project.video URL)
//   3. Static image (project.image)
//
// No embedded YouTube player inside the grid — per design, clicks take the
// user to the source. The thumbnail-cycle preview keeps the card alive on
// hover without the visual weight of an iframe.

const ytThumb = (id) => `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;
const ytFallbackThumb = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
const ytFrame = (id, i) => `https://i.ytimg.com/vi/${id}/${i}.jpg`;

const FRAME_INTERVAL_MS = 650; // how fast the GIF preview cycles

export const ProjectCard = ({ project, index = 0 }) => {
    const ref = useRef(null);
    const isMobile = useIsMobile();
    const [hovered, setHovered] = useState(false);
    const [frame, setFrame] = useState(1);

    const hasYtId = Boolean(project.youtubeId);
    const externalUrl = project.externalUrl || project.youtubeUrl || null;
    const isExternal = Boolean(externalUrl);

    const [thumbSrc, setThumbSrc] = useState(
        hasYtId ? ytThumb(project.youtubeId) : project.image
    );

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"]
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

    // Preload storyboard frames once for smooth crossfades on hover.
    useEffect(() => {
        if (!hasYtId || isMobile) return;
        [1, 2, 3].forEach((i) => {
            const img = new Image();
            img.src = ytFrame(project.youtubeId, i);
        });
    }, [hasYtId, isMobile, project.youtubeId]);

    // Cycle frames only while hovered.
    useEffect(() => {
        if (!hovered || !hasYtId || isMobile) {
            setFrame(1);
            return undefined;
        }
        const id = setInterval(() => {
            setFrame((f) => (f % 3) + 1);
        }, FRAME_INTERVAL_MS);
        return () => clearInterval(id);
    }, [hovered, hasYtId, isMobile]);

    const onThumbError = () => {
        if (hasYtId && thumbSrc === ytThumb(project.youtubeId)) {
            setThumbSrc(ytFallbackThumb(project.youtubeId));
        }
    };

    const openInNewTab = (e) => {
        if (!isExternal) return;
        if (e.metaKey || e.ctrlKey) return;
        e.preventDefault();
        window.open(externalUrl, "_blank", "noopener,noreferrer");
    };

    const previewActive = hasYtId && hovered && !isMobile;

    const CardMedia = (
        <div
            data-cursor="play"
            className="relative w-full overflow-hidden rounded-[2px] border border-white/10 bg-ink-surface aspect-[16/9] transition-colors duration-500 group-hover:border-[#DB162F]/60"
        >
            {/* Base media — YouTube thumbnail, MP4, or static image */}
            {project.video ? (
                <video
                    data-testid={`project-video-${project.id}`}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.06]"
                    src={project.video}
                    autoPlay
                    muted
                    loop
                    playsInline
                    poster={project.image}
                />
            ) : (
                <motion.img
                    data-testid={`project-image-${project.id}`}
                    src={thumbSrc}
                    alt={`${project.title} — ${project.category}`}
                    loading="lazy"
                    decoding="async"
                    onError={onThumbError}
                    style={{ y: imgY }}
                    className="kenburns absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.06]"
                    draggable={false}
                />
            )}

            {/* GIF-like preview — three stacked thumbnail frames that crossfade.
                Only one is visible at a time while hovered. */}
            {previewActive ? (
                <div className="pointer-events-none absolute inset-0">
                    {[1, 2, 3].map((i) => (
                        <img
                            key={i}
                            src={ytFrame(project.youtubeId, i)}
                            alt=""
                            aria-hidden
                            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
                            style={{ opacity: frame === i ? 1 : 0 }}
                            draggable={false}
                        />
                    ))}
                </div>
            ) : null}

            {/* Hover overlay: dark gradient + red play arrow only */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/10 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"
            />

            {/* Arrow only (no title text on hover) */}
            <div className="pointer-events-none absolute inset-x-5 bottom-5 z-10 flex justify-end md:inset-x-7 md:bottom-7">
                <span
                    className="flex h-11 w-11 translate-y-6 items-center justify-center rounded-full border border-white bg-[#DB162F] text-white opacity-0 transition-all duration-500 ease-out group-hover:translate-y-0 group-hover:opacity-100 md:h-14 md:w-14"
                    aria-hidden
                >
                    <ArrowUpRight className="h-4 w-4 md:h-5 md:w-5" />
                </span>
            </div>

            {/* Hover border pulse */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 border border-white/0 transition-[border-color] duration-500 group-hover:border-white/25"
            />

            {/* Subtle red hover glow */}
            <div
                aria-hidden
                className="pointer-events-none absolute -inset-[1px] rounded-[2px] opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                style={{
                    boxShadow:
                        "0 0 28px 2px rgba(219,22,47,0.45), inset 0 0 20px rgba(219,22,47,0.18)"
                }}
            />
        </div>
    );

    const MetaRow = (
        <div className="flex items-baseline justify-between gap-3">
            <div className="flex min-w-0 items-baseline gap-2">
                <span className="font-mono text-[10px] tracking-[0.25em] text-fog-muted md:text-xs">
                    /{String(index + 1).padStart(2, "0")}
                </span>
                <h4
                    className="truncate font-display text-lg uppercase tracking-tight text-fog-primary md:text-xl"
                    title={project.title}
                >
                    {project.client || project.title}
                </h4>
            </div>
            <span className="inline-flex items-center gap-2 whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.3em] text-fog-muted md:text-xs">
                <span className="inline-block h-1 w-1 rotate-45 bg-[#DB162F]" />
                {project.category}
            </span>
        </div>
    );

    if (isExternal) {
        return (
            <motion.article
                ref={ref}
                data-testid={`project-card-${project.id}`}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                className="group relative flex flex-col gap-3"
            >
                <a
                    href={externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={openInNewTab}
                    aria-label={`Watch ${project.title} (opens in new tab)`}
                    className="block"
                >
                    {CardMedia}
                </a>
                {MetaRow}
            </motion.article>
        );
    }

    return (
        <motion.article
            ref={ref}
            data-testid={`project-card-${project.id}`}
            className="group relative flex flex-col gap-3"
        >
            {CardMedia}
            {MetaRow}
        </motion.article>
    );
};

export default ProjectCard;
